// Kontakte (Agent 5C, Runde 5): alle Kontakte aller Leads mit Name, Telefon/E-Mail als Symbole, Funktion und Lead (C01, C02).
// Suche (Name, Funktion, Lead, Nummer, Adresse), Filter nach Lead-Status und „hat Telefon/E-Mail“, Sortierung und Spalten je
// Nutzer gemerkt, Markieren (Kästchen, x) mit Kopieren der Adressen und Nummern und CSV. Die Bedienleiste kommt von Agent 5B.
// Route `/kontakte`, Client `api/kontakte.ts`, Backend `routers/kontakte.py` (API.md 20.3). Ein Klick öffnet den Lead mit dem Kontakt.
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { kontakteApi, type KontakteSortierung, type KontaktZeile } from "../api/kontakte";
import { Auswahl } from "../components/Auswahl";
import { KopierKnopf, Laden, Leer, StatusPille } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeldung } from "../components/Meldungen";
import { Telefonnummer } from "../components/Telefonnummer";
import { telefonText } from "../werkzeuge/telefon";
import { Bedienleiste } from "../components/smartview/Bedienleiste";
import { csvSchreiben, dateiSpeichern } from "../components/smartview/csv";
import { ListenSchluessel } from "../components/smartview/listenVertrag";
import { useMeta } from "../daten/meta";
import { emailVerfassen } from "../daten/schreiben";
import { useSeitenKopf } from "../daten/seite";
import { KontaktWege } from "../kontakte/KontaktWege";
import { csvZeilen, mailAdressen, telefonNummern } from "../kontakte/kontakteExport";
import {
  ersteRichtungAb,
  KONTAKT_SORT_FELD,
  KONTAKT_SPALTEN,
  KONTAKT_STANDARD,
  KONTAKT_STANDARD_SORT,
  KONTAKTWEGE,
  kontaktName,
} from "../kontakte/kontaktSpalten";
import { spaltenWaehlen, sortAusParam, sortFuerRoute, sortUmschalten, sortZuParam } from "../kontakte/liste/ansicht";
import { useGemerkteAnsicht } from "../kontakte/liste/gemerkteAnsicht";
import { AUSWAHL_SPALTE, ListenTabelle, type TabellenSpalte } from "../kontakte/liste/ListenTabelle";
import { useSpaltenLayout } from "../components/spaltenLayout";
import { KompaktWert } from "../kontakte/liste/KompaktWert";
import { useListe } from "../kontakte/liste/useListe";
import { T } from "../texte";
import { kopieren, useTasten } from "../werkzeuge/tasten";
import { berlinDatum, datumText, gueltig, zeitpunktLang } from "../werkzeuge/zeit";
import "../kontakte/liste/liste.css";
import "../kontakte/kontakte.css";

const SORT_SCHLUESSEL = KONTAKT_SPALTEN.filter((s) => s.sortierbar).map((s) => s.key);
const KLASSEN: Record<string, string> = {
  name: "spalte-name",
  [KONTAKTWEGE]: "spalte-wege",
  title: "spalte-titel",
  telefon: "spalte-telefon",
  email: "spalte-mail",
  lead: "spalte-lead",
  status: "spalte-status",
  date_created: "spalte-datum",
  date_updated: "spalte-datum",
};

type Dreiwert = "ja" | "nein" | null;
const dreiwert = (w: string | null): Dreiwert => (w === "ja" || w === "nein" ? w : null);
const alsBool = (w: Dreiwert): boolean | undefined => (w === "ja" ? true : w === "nein" ? false : undefined);

const AUSWAHL_DEF: TabellenSpalte = { key: AUSWAHL_SPALTE, label: "", kopfTitel: "Auswahl", klasse: "liste-auswahlspalte" };

function zahlWort(n: number, eins: string, viele: string): string {
  return `${n.toLocaleString("de-DE")} ${n === 1 ? eins : viele}`;
}

export default function Kontakte() {
  useSeitenKopf([{ label: T.nav.kontakte }]);
  const meta = useMeta();
  const navigate = useNavigate();
  const meldung = useMeldung();
  const [params, setParams] = useSearchParams();

  const q = params.get("q") ?? "";
  const leadId = params.get("lead") ?? "";
  const statusIds = useMemo(() => (params.get("status") ?? "").split(",").filter(Boolean), [params]);
  const telefon = dreiwert(params.get("telefon"));
  const email = dreiwert(params.get("email"));
  const sortParam = params.get("sort");
  const [eingabe, setEingabe] = useState(q);
  const [fokus, setFokus] = useState(0);
  const [markiert, setMarkiert] = useState<Set<string>>(() => new Set());

  // Gemerkte Ansicht (Spalten, Sortierung) je Nutzer
  const gemerkt = useGemerkteAnsicht(ListenSchluessel.kontakte);
  const [spaltenWahl, setSpaltenWahl] = useState<string[] | null>(null);
  const [sortWahl, setSortWahl] = useState<ReturnType<typeof sortAusParam> | "standard" | null>(null);
  const gezeigt = useMemo(
    () => spaltenWaehlen(spaltenWahl ?? gemerkt.ansicht?.spalten, KONTAKT_SPALTEN, KONTAKT_STANDARD),
    [spaltenWahl, gemerkt.ansicht?.spalten],
  );
  const layout = useSpaltenLayout(gemerkt.zugriff, gezeigt, (keys) => {
    setSpaltenWahl(keys);
    gemerkt.merken({ spalten: keys });
  });
  const gespeicherteSort = gemerkt.ansicht?.sortierung?.[0];
  const sort = useMemo(() => {
    const ausAdresse = sortAusParam(sortParam, SORT_SCHLUESSEL, { key: "", ab: false });
    if (ausAdresse.key) return ausAdresse;
    if (sortWahl === "standard") return KONTAKT_STANDARD_SORT;
    if (sortWahl) return sortWahl;
    if (gespeicherteSort && SORT_SCHLUESSEL.includes(gespeicherteSort.key)) return { key: gespeicherteSort.key, ab: gespeicherteSort.ab };
    return KONTAKT_STANDARD_SORT;
  }, [sortParam, sortWahl, gespeicherteSort]);

  const setParam = useCallback(
    (aenderungen: Record<string, string | null>) => {
      setParams(
        (alt) => {
          const neu = new URLSearchParams(alt);
          for (const [k, v] of Object.entries(aenderungen)) {
            if (v) neu.set(k, v);
            else neu.delete(k);
          }
          return neu;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // Suche verzögert in die Adresse schreiben
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (eingabe.trim() !== q) setParam({ q: eingabe.trim() || null });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingabe]);
  // Adresse von außen geändert (Zurück-Taste): Eingabefeld nachziehen
  useEffect(() => {
    setEingabe((alt) => (alt.trim() === q ? alt : q));
  }, [q]);

  const sortRoute = sortFuerRoute(sort, KONTAKT_SORT_FELD) as KontakteSortierung | undefined;
  const schluessel = JSON.stringify([q, leadId, statusIds, telefon, email, sortRoute]);
  const holen = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      kontakteApi.liste(
        { limit: 100, cursor, q: q || undefined, lead_id: leadId || undefined, status_id: statusIds, telefon: alsBool(telefon), email: alsBool(email), sort: sortRoute },
        signal,
      ),
    [q, leadId, statusIds, telefon, email, sortRoute],
  );
  const liste = useListe<KontaktZeile>(holen, schluessel);
  const { zeilen } = liste;

  useEffect(() => {
    setMarkiert(new Set());
    setFokus(0);
  }, [schluessel]);
  // Esc hebt die Markierung auf
  useTasten({ Escape: () => setMarkiert(new Set()) }, markiert.size > 0);

  const nameVonId = useMemo(() => new Map(zeilen.map((z) => [z.id, kontaktName(z.name, z.phones, z.emails).text])), [zeilen]);
  const oeffnen = (z: KontaktZeile) => navigate(`/lead/${encodeURIComponent(z.lead_id)}?kontakt=${encodeURIComponent(z.id)}`);
  const leadOeffnen = (z: KontaktZeile) => navigate(`/lead/${encodeURIComponent(z.lead_id)}`);

  // Spalten: Markierungskästchen vorn, dann die gewählten
  const spalten: TabellenSpalte[] = useMemo(
    () => [
      AUSWAHL_DEF,
      ...gezeigt.flatMap((key) => {
        const def = KONTAKT_SPALTEN.find((s) => s.key === key);
        if (!def) return [];
        return [
          {
            key,
            label: key === KONTAKTWEGE ? "" : def.label,
            kopfTitel: key === KONTAKTWEGE ? "Telefon und E-Mail" : undefined,
            sortierbar: !!def.sortierbar,
            fest: key === KONTAKTWEGE,
            klasse: KLASSEN[key],
          } as TabellenSpalte,
        ];
      }),
    ],
    [gezeigt],
  );

  const sortKlick = (key: string) => {
    const neu = sortUmschalten(sort, key, ersteRichtungAb(key));
    setSortWahl(neu);
    setParam({ sort: sortZuParam(neu, KONTAKT_STANDARD_SORT) });
    gemerkt.merken({ sortierung: [neu] });
  };

  const zelle = (z: KontaktZeile, s: TabellenSpalte) => {
    switch (s.key) {
      case "name": {
        const n = kontaktName(z.name, z.phones, z.emails);
        return (
          <a
            href={`/lead/${encodeURIComponent(z.lead_id)}?kontakt=${encodeURIComponent(z.id)}`}
            className={`tabelle__name${n.ohneName ? " tabelle__name--ersatz" : ""}`}
            title={n.ohneName ? `${n.text} (ohne Namen)` : n.text}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              oeffnen(z);
            }}
          >
            {n.text}
          </a>
        );
      }
      case KONTAKTWEGE:
        return <KontaktWege k={z} />;
      case "title":
        return z.title?.trim() ? <span title={z.title}>{z.title}</span> : null;
      case "lead":
        return z.lead_name ? (
          <a
            href={`/lead/${encodeURIComponent(z.lead_id)}`}
            className="kontakt-lead"
            title={z.lead_name}
            onClick={(e: MouseEvent<HTMLAnchorElement>) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              leadOeffnen(z);
            }}
          >
            {z.lead_name}
          </a>
        ) : (
          <span className="leer-wert">—</span>
        );
      case "status":
        return z.lead_status_label ? <StatusPille label={z.lead_status_label} farbe={meta.status(z.lead_status_id)?.color} klein schlicht /> : null;
      case "telefon": {
        const t = z.phones[0];
        if (!t) return <span className="leer-wert">—</span>;
        return (
          <span className="kontakt-mehrere">
            <Telefonnummer nummer={t.phone} formatiert={t.phone_formatted} leadId={z.lead_id} contactId={z.id} kompakt leadName={z.lead_name} kontaktName={z.name} />
            {z.phones.length > 1 ? <span className="zelle-mehr" title={z.phones.slice(1).map((x) => telefonText(x.phone, x.phone_formatted)).join(", ")}>+{z.phones.length - 1}</span> : null}
          </span>
        );
      }
      case "email": {
        const m = z.emails[0];
        if (!m) return <span className="leer-wert">—</span>;
        return (
          <span className="kontakt-mehrere">
            <span className="zelle-mail">
              <a
                href={`mailto:${m.email}`}
                title={`E-Mail schreiben: ${m.email}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (emailVerfassen({ leadId: z.lead_id, contactId: z.id, an: m.email, leadName: z.lead_name })) e.preventDefault();
                }}
              >
                {m.email}
              </a>
              <KopierKnopf text={m.email} label="E-Mail kopieren" />
            </span>
            {z.emails.length > 1 ? <span className="zelle-mehr" title={z.emails.slice(1).map((x) => x.email).join(", ")}>+{z.emails.length - 1}</span> : null}
          </span>
        );
      }
      case "date_created":
      case "date_updated": {
        const t = s.key === "date_created" ? z.date_created : z.date_updated;
        return gueltig(t) ? <span title={zeitpunktLang(t)}>{datumText(berlinDatum(t))}</span> : null;
      }
      default:
        return null;
    }
  };

  // Menü „…“: Link, Kopieren, CSV, Markierung
  const bezug = markiert.size ? zeilen.filter((z) => markiert.has(z.id)) : zeilen;
  const bezugText = markiert.size ? zahlWort(markiert.size, "markierter Kontakt", "markierte Kontakte") : `alle ${zahlWort(zeilen.length, "geladenen Kontakt", "geladenen Kontakte")}`;
  const kopierenMitMeldung = async (text: string, was: string, anzahl: number) => {
    if (!anzahl) {
      meldung.zeige("info", `Keine ${was} in ${bezugText}.`);
      return;
    }
    const ok = await kopieren(text);
    meldung.zeige(ok ? "ok" : "fehler", ok ? `${zahlWort(anzahl, was.replace(/n$/, ""), was)} kopiert.` : "Kopieren hat nicht geklappt.");
  };
  const mehrPunkte: MenuePunkt[] = [
    { label: "Link kopieren", icon: "link", onClick: () => void kopieren(window.location.href).then((ok) => meldung.zeige(ok ? "ok" : "fehler", ok ? "Link kopiert." : "Kopieren hat nicht geklappt.")) },
    { label: "Neu laden", icon: "neu-laden", onClick: () => liste.neuLaden(true) },
    {
      label: "E-Mail-Adressen kopieren",
      icon: "mail",
      hinweis: `Adressen von ${bezugText}, abgemeldete ausgenommen`,
      trenner: true,
      deaktiviert: !zeilen.length,
      onClick: () => {
        const a = mailAdressen(bezug);
        void kopierenMitMeldung(a.join("\n"), "E-Mail-Adressen", a.length);
      },
    },
    {
      label: "Telefonnummern kopieren",
      icon: "telefon",
      hinweis: `Nummern von ${bezugText}`,
      deaktiviert: !zeilen.length,
      onClick: () => {
        const n = telefonNummern(bezug);
        void kopierenMitMeldung(n.join("\n"), "Telefonnummern", n.length);
      },
    },
    {
      label: "Als CSV speichern",
      icon: "kopieren",
      hinweis: `${bezugText} als Datei`,
      deaktiviert: !zeilen.length,
      onClick: () => {
        dateiSpeichern(`kontakte-${new Date().toISOString().slice(0, 10)}.csv`, csvSchreiben(csvZeilen(bezug)));
        meldung.ok(`${zahlWort(bezug.length, "Kontakt", "Kontakte")} gespeichert.`);
      },
    },
    ...(zeilen.length ? [{ label: "Alle geladenen markieren", icon: "haken", trenner: true, onClick: () => setMarkiert(new Set(zeilen.map((z) => z.id))) } as MenuePunkt] : []),
    ...(markiert.size ? [{ label: `Markierung aufheben (${markiert.size})`, icon: "schliessen", onClick: () => setMarkiert(new Set()) } as MenuePunkt] : []),
  ];

  const statusOptionen = (meta.meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }));
  const filterAktiv = !!(q || leadId || statusIds.length || telefon || email);
  const leadFilterName = leadId ? zeilen[0]?.lead_name ?? "Lead" : null;

  return (
    <div className="liste-seite">
      <div className="liste-kopf">
        <h1 className="liste-kopf__titel">{T.nav.kontakte}</h1>
        <div className="liste-kopf__rechts">
          <MenueKnopf knopfKlasse="symbolknopf symbolknopf--rand" titel="Mehr" punkte={mehrPunkte} ausrichtung="end" breite={300}>
            <Icon name="mehr" groesse={16} />
          </MenueKnopf>
        </div>
      </div>
      <Bedienleiste
        liste={ListenSchluessel.kontakte}
        einheit={{ eins: "Kontakt", viele: "Kontakte" }}
        trefferZahl={liste.total}
        geladen={zeilen.length}
        laedt={liste.laedt}
        spalten={KONTAKT_SPALTEN}
        gezeigt={gezeigt}
        onSpalten={(keys) => {
          setSpaltenWahl(keys ?? spaltenWaehlen(null, KONTAKT_SPALTEN, KONTAKT_STANDARD));
          gemerkt.merken({ spalten: keys });
        }}
        sort={sort.key === KONTAKT_STANDARD_SORT.key && sort.ab === KONTAKT_STANDARD_SORT.ab ? null : sort}
        sortStandard={[KONTAKT_STANDARD_SORT]}
        sortStandardName="Name von A bis Z"
        onSort={(s) => {
          setSortWahl(s ?? "standard");
          setParam({ sort: s ? sortZuParam(s, KONTAKT_STANDARD_SORT) : null });
          gemerkt.merken({ sortierung: s ? [s] : null });
        }}
        suche={{ wert: eingabe, onAendern: setEingabe, platzhalter: "Name, Funktion, Lead, Telefon, E-Mail …" }}
        auswahl={{ anzahl: markiert.size, onAlleMarkieren: zeilen.length ? () => setMarkiert(new Set(zeilen.map((z) => z.id))) : undefined, onAufheben: () => setMarkiert(new Set()) }}
        spaltenStandard={KONTAKT_STANDARD}
        hinweis={
        <span className="kontakte-filter">
          <span className="kontakte-filter__status">
            <Auswahl
              optionen={statusOptionen}
              wert={statusIds}
              mehrfach
              leerErlaubt
              platzhalter="Alle Lead-Status"
              breite={260}
              titel="Nach Status des Leads filtern"
              knopfInhalt={statusIds.length > 1 ? <KompaktWert text={`${statusIds.length} Lead-Status`} /> : undefined}
              onWahl={(w) => setParam({ status: (w as string[]).join(",") || null })}
            />
          </span>
          <span className="kontakte-filter__klein">
            <Auswahl
              optionen={[
                { wert: "ja", label: "Mit Telefonnummer" },
                { wert: "nein", label: "Ohne Telefonnummer" },
              ]}
              wert={telefon}
              leerErlaubt
              platzhalter="Telefon"
              breite={190}
              titel="Nach vorhandener Telefonnummer filtern"
              onWahl={(w) => setParam({ telefon: (w as string | null) || null })}
            />
          </span>
          <span className="kontakte-filter__klein">
            <Auswahl
              optionen={[
                { wert: "ja", label: "Mit E-Mail-Adresse" },
                { wert: "nein", label: "Ohne E-Mail-Adresse" },
              ]}
              wert={email}
              leerErlaubt
              platzhalter="E-Mail"
              breite={190}
              titel="Nach vorhandener E-Mail-Adresse filtern"
              onWahl={(w) => setParam({ email: (w as string | null) || null })}
            />
          </span>
          {leadId ? (
            <span className="chip kontakte-filter__lead">
              Lead: {leadFilterName}
              <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Lead-Filter entfernen" onClick={() => setParam({ lead: null })}>
                <Icon name="schliessen" groesse={12} />
              </button>
            </span>
          ) : null}
          {filterAktiv ? (
            <button
              type="button"
              className="knopf knopf--leise knopf--klein"
              onClick={() => {
                setEingabe("");
                setParam({ q: null, lead: null, status: null, telefon: null, email: null });
              }}
            >
              Filter leeren
            </button>
          ) : null}
        </span>
        }
      />
      {liste.fehler ? <FehlerBox fehler={liste.fehler} onNochmal={() => liste.neuLaden()} /> : null}
      {liste.laedt && !zeilen.length ? (
        <Laden />
      ) : !liste.fehler && !zeilen.length ? (
        <Leer
          titel="Keine Kontakte gefunden."
          text={filterAktiv ? "Suche oder Filter anpassen." : "Die lokale Kopie hat noch keine Kontakte. Der Abgleich mit Close füllt sie."}
        />
      ) : zeilen.length ? (
        <ListenTabelle<KontaktZeile>
          klasse="tabelle--kontakte"
          breiten={layout.breiten}
          onBreite={layout.onBreite}
          onVerschieben={layout.onVerschieben}
          spalten={spalten}
          zeilen={zeilen}
          zeilenId={(z) => z.id}
          zelle={zelle}
          fokus={Math.min(fokus, zeilen.length - 1)}
          setFokus={setFokus}
          onOeffnen={oeffnen}
          sort={sort}
          onSort={sortKlick}
          onEnde={liste.hatMehr ? liste.mehr : undefined}
          auswahl={{
            markiert,
            onAendern: setMarkiert,
            beschriftung: (id) => `${nameVonId.get(id) ?? "Kontakt"} markieren`,
            alleTitel: "Alle geladenen Kontakte markieren",
          }}
          fuss={
            liste.hatMehr ? (
              <div className="tabelle__mehr">
                <button type="button" className="knopf" disabled={liste.laedtMehr} onClick={liste.mehr}>
                  {liste.laedtMehr ? "Lädt …" : "Weitere laden"}
                </button>
              </div>
            ) : null
          }
        />
      ) : null}
    </div>
  );
}
